"use client";

import { type ComponentProps, type ReactNode, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Box, styled } from "@mui/material";
import { Tabs, TabList, Tab, TabPanel } from "@/ui";
import SummaryTab from "./Summary/SummaryTab";
import NotesTab from "./Notes/NotesTab";
import FilesTab from "./Files/FilesTab";
import ImageUpload from "../modals/parts/ImageUpload";
import { CandidateDots } from "@/components/Signals/CandidateDots";
import { useSignalsAccess } from "@/lib/signals/useSignals";
import useRecruiterDetails from "@/lib/user/useRecruiterDetails";
import { hasFeatureAccess, FLAG_LEVELS } from "@/lib/featureFlags";

const AvatarPanel = styled(Box)({
  width: "200px",
});

const TabsSection = styled(Box)({
  background: "var(--bg-surface)",
  borderRadius: "var(--radius-md)",
  boxShadow: "var(--shadow-1)",
  overflow: "hidden",
  paddingTop: "var(--space-5)",
});

const TabStrip = styled(Box)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const Panel = styled(Box)({
  padding: "0 32px 32px",
});

type CandidateTabsProps = ComponentProps<typeof FilesTab> & {
  candidate: { id: string | number; name?: string; photo_url?: string; [key: string]: unknown };
  candidateId: string | number;
  profile: ReactNode;
  onImageUpdate: ComponentProps<typeof ImageUpload>["onUpdate"];
  onAvatarFromLibrary: ComponentProps<typeof ImageUpload>["onSelectFromLibrary"];
  isGeneratingSummary?: boolean;
};

export default function CandidateTabs({
  candidate,
  candidateId,
  profile,
  onImageUpdate,
  onAvatarFromLibrary,
  onDownloadOriginal,
  onDownloadFormatted,
  onDownloadFormattedOriginal,
  hasResume,
  hasRawResume,
  isGeneratingSummary,
}: CandidateTabsProps) {
  const searchParams = useSearchParams();
  const { recruiter } = useRecruiterDetails();
  const { enabled: signalsAccess } = useSignalsAccess();
  const signalsEnabled = signalsAccess && hasFeatureAccess(FLAG_LEVELS.FLOATS, recruiter?.user);
  const [tab, setTab] = useState(() => searchParams.get("tab") || "summary");

  // Deep-links from the Float Score checklist ("Add" links) pass ?tab=... and
  // a #candidate-section-* hash — scroll to that section once its tab has
  // actually mounted (TabPanel unmounts inactive tabs, so the target isn't in
  // the DOM until then).
  useEffect(() => {
    if (typeof window === "undefined" || !window.location.hash) return;
    const id = window.location.hash.slice(1);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [tab]);

  return (
    <TabsSection>
      <Tabs value={tab} onChange={setTab}>
        <TabStrip>
          <TabList>
            <Tab value="summary">AI summary</Tab>
            <Tab value="profile">Profile details</Tab>
            <Tab value="avatar">Avatar</Tab>
            <Tab value="notes">Notes</Tab>
            <Tab value="files">Files</Tab>
            {signalsEnabled && <Tab value="dots">DOTS</Tab>}
          </TabList>
        </TabStrip>
        <Panel>
          <TabPanel value="summary">
            <SummaryTab
              candidate={candidate}
              isGeneratingSummary={isGeneratingSummary}
            />
          </TabPanel>
          <TabPanel value="profile">{profile}</TabPanel>
          <TabPanel value="avatar">
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <AvatarPanel>
                <ImageUpload
                  imageUrl={candidate.photo_url}
                  name={candidate.name}
                  onUpdate={onImageUpdate}
                  onSelectFromLibrary={onAvatarFromLibrary}
                />
              </AvatarPanel>
            </Box>
          </TabPanel>
          <TabPanel value="notes">
            <NotesTab candidateId={candidateId} />
          </TabPanel>
          <TabPanel value="files">
            <FilesTab
              onDownloadOriginal={onDownloadOriginal}
              onDownloadFormatted={onDownloadFormatted}
              onDownloadFormattedOriginal={onDownloadFormattedOriginal}
              hasResume={hasResume}
              hasRawResume={hasRawResume}
            />
          </TabPanel>
          {signalsEnabled && (
            <TabPanel value="dots">
              {/* The page is addressed by uuid; DOTS and the Signals API use the numeric record id. */}
              <CandidateDots candidateId={String(candidate.id)} />
            </TabPanel>
          )}
        </Panel>
      </Tabs>
    </TabsSection>
  );
}
